import React, { useState, useCallback, useEffect } from "react";
import {
    Box, Typography, TextField, Alert, CircularProgress,
    FormGroup, Checkbox, FormControlLabel, RadioGroup, Radio,
    FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { axios_tenant } from "../../../../axios";
import { BRAND_COLORS, REQUEST_TYPES, PGS_SUPPORT_TYPES, PROOFREADING_SCOPES } from "../constants/taskStatuses";
import Button from "./atoms/Button";

const BASE_URL = "/collaboration/workflow";

/**
 * SubmitRequestView — Self-contained form for client-submitted OA Shell requests.
 * Handles request type selection, application number input, and submission.
 */
const SubmitRequestView = ({ workType = "OA Shell", onSuccess }) => {
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [applicationInput, setApplicationInput] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [skippedList, setSkippedList] = useState([]);
    const [requestLimit, setRequestLimit] = useState(100);
    const [proofreadingScope, setProofreadingScope] = useState("Entire");

    const enabledTypes = REQUEST_TYPES.filter(t => t.enabled);

    // Fetch user config (including request limit) on mount
    useEffect(() => {
        axios_tenant.get(`${BASE_URL}/me?workType=${encodeURIComponent(workType)}`, { silent: true })
            .then(res => {
                if (res.data?.clientRequestLimit) {
                    setRequestLimit(res.data.clientRequestLimit);
                }
            })
            .catch(err => {
                console.error("Failed to fetch client request limit:", err);
            });
    }, []);

    // Initialize selectedTypes based on workType
    useEffect(() => {
        if (workType === "OA Shell") {
            setSelectedTypes(["oa_shell"]);
        } else {
            setSelectedTypes([]);
        }
        setErrorMessage("");
        setSuccessMessage("");
        setSkippedList([]);
        setApplicationInput("");
    }, [workType]);

    const handleTypeToggle = (typeId) => {
        setSelectedTypes(prev => {
            if (prev.includes(typeId)) {
                return prev.filter(id => id !== typeId);
            }
            return [...prev, typeId];
        });
        setErrorMessage("");
        setSuccessMessage("");
        setSkippedList([]);
    };

    const isOAShellSelected = selectedTypes.includes("oa_shell");
    const selectedPgsTypes = selectedTypes.filter(id => {
        const typeDef = REQUEST_TYPES.find(t => t.id === id);
        return typeDef?.pgs;
    });
    const hasPgsSelected = selectedPgsTypes.length > 0;
    const hasProofreadingSelected = selectedTypes.includes("proofreading");

    /**
     * Parse comma/newline-separated application numbers.
     * Enforces single application number limit for OA Shell.
     */
    const parseApplicationNumbers = (input) => {
        if (!input) return [];
        const normalized = input.replace(/[;\r\n]+/g, "\n");
        const lines = normalized.split("\n");
        const result = [];
        for (let line of lines) {
            line = line.trim();
            if (!line) continue;
            const parts = [];
            let currentStart = 0;
            for (let i = 0; i < line.length; i++) {
                if (line[i] === ',') {
                    const beforeStr = line.substring(0, i).replace(/\s/g, "");
                    const afterStr = line.substring(i + 1).trim();
                    const prefixDigits = line.substring(currentStart, i).replace(/[^0-9]/g, "");
                    const hasDigitBefore = /\d$/.test(beforeStr);
                    const hasThreeDigitsAfter = /^\d{3}(?!\d)/.test(afterStr);
                    const isPrefixValid = prefixDigits.length === 4 || prefixDigits.length === 5;
                    if (hasDigitBefore && hasThreeDigitsAfter && isPrefixValid) {
                        continue;
                    } else {
                        parts.push(line.substring(currentStart, i));
                        currentStart = i + 1;
                    }
                }
            }
            parts.push(line.substring(currentStart));
            for (let part of parts) {
                const cleaned = part.replace(/[^0-9]/g, "");
                if (cleaned) {
                    result.push(cleaned);
                }
            }
        }
        return result;
    };

    const handleSubmit = useCallback(async () => {
        setErrorMessage("");
        setSuccessMessage("");
        setSkippedList([]);

        if (!isOAShellSelected && !hasPgsSelected) {
            setErrorMessage("Please select a request type.");
            return;
        }

        const rawNumbers = parseApplicationNumbers(applicationInput);
        if (rawNumbers.length === 0) {
            setErrorMessage("Please enter an application number.");
            return;
        }

        if (hasPgsSelected) {
            if (rawNumbers.length > 500) {
                setErrorMessage("Maximum 500 application numbers allowed per request");
                return;
            }
        }

        // OA Shell path: single app number, 8 digits
        if (isOAShellSelected && !hasPgsSelected) {
            if (rawNumbers.length > 1) {
                setErrorMessage("Only one application number allowed per request");
                return;
            }
            const simplifiedNumber = applicationInput.trim().replace(/[^0-9]/g, "");
            if (!simplifiedNumber || simplifiedNumber.length !== 8) {
                setErrorMessage("The input application number is invalid. Please try again with a valid 8-digit number.");
                return;
            }

            setSubmitting(true);
            try {
                const res = await axios_tenant.post(`${BASE_URL}/requests/oa-shell`, {
                    applicationNumbers: [simplifiedNumber]
                });
                const count = res.data?.tasks?.length || 0;
                setSkippedList(res.data?.duplicates || []);
                setSuccessMessage(count > 0 ? `${count} request submitted successfully.` : "");
                setApplicationInput("");
                setTimeout(() => onSuccess?.(), 1500);
            } catch (err) {
                const data = err?.response?.data;
                setSkippedList(data?.duplicates || []);
                setErrorMessage(
                    (data?.duplicates?.length > 0 && !data?.tasks?.length)
                        ? "This application number already has a pending task:"
                        : (data?.message || "Failed to submit request. Please try again.")
                );
            } finally {
                setSubmitting(false);
            }
            return;
        }

        // PGS path: multi-select support types × application numbers
        const supportTypes = selectedPgsTypes.map(id => PGS_SUPPORT_TYPES[id]).filter(Boolean);
        if (supportTypes.length === 0) {
            setErrorMessage("Please select at least one support type.");
            return;
        }

        setSubmitting(true);
        try {
            const res = await axios_tenant.post(`${BASE_URL}/requests/pgs`, {
                applicationNumbers: rawNumbers,
                supportTypes,
                ...(hasProofreadingSelected && proofreadingScope && { proofreadingScope })
            });
            const count = res.data?.tasks?.length || 0;
            setSkippedList(res.data?.duplicates || []);
            setSuccessMessage(
                count > 0
                    ? `${count} PGS request${count === 1 ? "" : "s"} submitted successfully.`
                    : ""
            );
            setApplicationInput("");
            setSelectedTypes([]);
            setProofreadingScope("Entire");
            setTimeout(() => onSuccess?.(), 1500);
        } catch (err) {
            const data = err?.response?.data;
            setSkippedList(data?.duplicates || []);
            setErrorMessage(data?.message || "Failed to submit PGS request. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }, [applicationInput, isOAShellSelected, hasPgsSelected, selectedPgsTypes, proofreadingScope, hasProofreadingSelected]);

    return (
        <Box
            sx={{
                backgroundColor: "#F8FAFC",
                borderRadius: "12px",
                p: { xs: 3, md: 4 },
                minHeight: 400,
                position: "relative"
            }}
            data-testid="submit-request-view"
        >
            {/* Header */}
            {/* <Typography
                sx={{
                    textAlign: "right",
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#94A3B8",
                    mb: 3
                }}
            >
                Submit a new request
            </Typography> */}

            {/* Step 1: Request Type Selection (PGS only) */}
            {workType === "Post Grant Support" && (
                <>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: "#64748B",
                            fontWeight: 500,
                            mb: 1.5,
                            letterSpacing: "0.01em"
                        }}
                    >
                        Select request type
                    </Typography>

                    <Box
                        sx={{
                            backgroundColor: "#fff",
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                            p: 2,
                            mb: 3
                        }}
                    >
                        <FormGroup row sx={{ gap: 2 }}>
                            {enabledTypes.filter(t => t.pgs).map(type => {
                                const isActive = selectedTypes.includes(type.id);
                                return (
                                    <FormControlLabel
                                        key={type.id}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={isActive}
                                                onChange={() => handleTypeToggle(type.id)}
                                                data-testid={`request-type-${type.id}`}
                                            />
                                        }
                                        label={<span style={{ fontSize: "13px", fontWeight: 600 }}>{type.label}</span>}
                                    />
                                );
                            })}
                        </FormGroup>
                    </Box>
                </>
            )}

            {/* Step 2: Application Number Input (shown when any type is selected) */}
            {(isOAShellSelected || hasPgsSelected) && (
                <>
                    {/* Proofreading scope selector */}
                    {hasProofreadingSelected && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: "14px", color: "#64748B", fontWeight: 500, mb: 1 }}>
                                Select proofreading scope
                            </Typography>
                            <RadioGroup
                                row
                                value={proofreadingScope}
                                onChange={(e) => setProofreadingScope(e.target.value)}
                            >
                                {PROOFREADING_SCOPES.map(s => (
                                    <FormControlLabel
                                        key={s}
                                        value={s}
                                        control={<Radio size="small" />}
                                        label={<span style={{ fontSize: "12px" }}>{s}</span>}
                                    />
                                ))}
                            </RadioGroup>
                        </Box>
                    )}

                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: "#64748B",
                            fontWeight: 500,
                            mb: 1.5,
                            letterSpacing: "0.01em"
                        }}
                    >
                        {hasPgsSelected
                            ? "Please enter the application numbers separated by new line(s)."
                            : "Enter application number"
                        }
                    </Typography>

                    <Box
                        sx={{
                            backgroundColor: "#fff",
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                            p: 2,
                            mb: 2
                        }}
                    >
                        <TextField
                            multiline
                            minRows={3}
                            maxRows={8}
                            fullWidth
                            variant="standard"
                            placeholder={hasPgsSelected
                                ? "Please enter the application numbers separated by new line(s)."
                                : "Enter application number (8 digits)"
                            }
                            value={applicationInput}
                            onChange={(e) => {
                                setApplicationInput(e.target.value);
                                setErrorMessage("");
                                setSuccessMessage("");
                            }}
                            disabled={submitting}
                            InputProps={{
                                disableUnderline: true,
                                sx: { fontSize: "14px", color: "#334155" }
                            }}
                            data-testid="application-number-input"
                        />

                        {/* Submit Footer */}
                        <Box
                            sx={{
                                borderTop: "1px solid #E2E8F0",
                                mt: 1.5,
                                pt: 1.5,
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: 1
                            }}
                        >
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || !applicationInput.trim()}
                                sx={{
                                    px: 3,
                                    gap: 1,
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    height: "38px"
                                }}
                                data-testid="submit-request-btn"
                            >
                                {submitting ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <>
                                        Submit
                                        <SendIcon sx={{ fontSize: 16 }} />
                                    </>
                                )}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}

            {/* Feedback Messages */}
            {successMessage && (
                <Alert
                    severity="success"
                    sx={{ mt: 2, borderRadius: "8px" }}
                    data-testid="submit-success-msg"
                >
                    {successMessage}
                    {skippedList.length > 0 && (
                        <Box sx={{ mt: 0.5, fontSize: "13px", color: "#4B5563" }}>
                            already had a pending task (skipped):{" "}
                            <strong>
                                {skippedList.map(item => 
                                    (typeof item === 'object' && item !== null) 
                                        ? `${item.appNo} (${item.supportType})` 
                                        : item
                                ).join(", ")}
                            </strong>
                        </Box>
                    )}
                </Alert>
            )}
            {errorMessage && (
                <Alert
                    severity="error"
                    sx={{ mt: 2, borderRadius: "8px" }}
                    data-testid="submit-error-msg"
                >
                    {errorMessage}
                    {skippedList.length > 0 && (
                        <Box sx={{ mt: 0.5, fontSize: "13px" }}>
                            <strong>
                                {skippedList.map(item => 
                                    (typeof item === 'object' && item !== null) 
                                        ? `${item.appNo} (${item.supportType})` 
                                        : item
                                ).join(", ")}
                            </strong>
                        </Box>
                    )}
                </Alert>
            )}
        </Box>
    );
};

export default SubmitRequestView;
